import React from "react";
import { motion } from "framer-motion";
import { PhoneCall, UserX, Megaphone, Bot } from "lucide-react";

const PROBLEMS = [
  {
    icon: PhoneCall,
    title: "15–20 calls per day",
    body: "“Is it ready yet?” Every. Single. Day. Every call pulls your staff away from actual work. It's exhausting, it's expensive, and it never stops.",
    testId: "problem-card-1",
  },
  {
    icon: UserX,
    title: "No customer database",
    body: "You have no idea who your customers are or how to reach them. Every person who walks out the door is gone forever — and that's revenue you'll never see again.",
    testId: "problem-card-2",
  },
  {
    icon: Megaphone,
    title: "No way to bring them back",
    body: "Eid, Ramadan, end-of-season — your competitors are running campaigns while you're hoping customers remember you exist. Hope is not a marketing strategy.",
    testId: "problem-card-3",
  },
  {
    icon: Bot,
    title: "No AI working for you",
    body: "While other businesses automate follow-ups, personalise messages, and run smart campaigns — you're still doing everything manually. Your competition isn't sleeping. Your tools should not be either.",
    testId: "problem-card-4",
  },
];

export const Problem = () => {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="bg-[#F9FAFB] py-20 sm:py-28 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] ping-gradient-text mb-4">
            The everyday reality
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-[3.5rem] lg:leading-[1.05] font-black tracking-tight text-[#1A1A2E]">
            Sound familiar?
          </h2>
          <p className="mt-5 text-lg text-gray-600 max-w-2xl">
            If you run a service business, you're losing time and money every
            day to a handful of problems you've just accepted as normal.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7">
          {PROBLEMS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                data-testid={p.testId}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group bg-white border border-gray-200/80 rounded-2xl p-7 sm:p-8 hover:border-[#7B5EA7]/40 hover:shadow-xl hover:shadow-gray-200/60 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl ping-gradient flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Icon size={22} className="text-white" strokeWidth={2.2} />
                </div>
                <h3 className="text-xl font-bold text-[#1A1A2E] mb-2.5 tracking-tight">
                  {p.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-[15px]">
                  {p.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Problem;
